"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import {
  AccountFreshnessRow,
  BhavcopyFreshnessRow,
  DataFreshness,
  HttpError,
  Me,
  MvStatusRow,
  RefreshAccountOut,
  RefreshBhavcopyOut,
  RefreshMvsOut,
  api,
} from "@/lib/api";
import { Crumbs, Page, Pill, Section } from "@/components/ui";
import { DataTable, type ColumnConfig } from "@/components/DataTable";
import { fmtDate, fmtInt, fmtRupees } from "@/lib/fmt";

function daysAgo(iso: string | null, serverNowIso: string): number | null {
  if (!iso) return null;
  // Whole calendar-day difference, NOT a rounded datetime delta. Both
  // last_trade_date and server_now (SELECT NOW()) are DB-side (IST) values; we
  // compare only their DATE parts (first 10 chars) as UTC midnight. Using the
  // full server_now timestamp + Math.round made a SAME-DAY last trade flip to
  // "1d ago" once the clock passed midday (frac > 0.5 rounds up), and the
  // browser timezone shifted it further. Date-only diff => same day is "today"
  // all day, prior day is exactly "1d ago". (chakra_dashboard#150)
  const a = new Date(iso.slice(0, 10) + "T00:00:00Z").getTime();
  const b = new Date(serverNowIso.slice(0, 10) + "T00:00:00Z").getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

function AgeBadge({ days, warn }: { days: number | null; warn: number }) {
  if (days === null) return <Pill variant="outline">—</Pill>;
  if (days > warn) return <Pill variant="down">{days}d ago</Pill>;
  if (days > 1) return <Pill variant="accent">{days}d ago</Pill>;
  return <Pill variant="up">{days === 0 ? "today" : `${days}d ago`}</Pill>;
}

// Per-row action state — one entry per row, keyed by the row's stable
// identity. `null` = idle (default). Kept as separate maps per section
// so an Accounts refresh doesn't stomp on an MV refresh spinner.
type RefreshCell =
  | { kind: "loading"; label: string }
  | { kind: "ok"; label: string; hint?: string }
  | { kind: "err"; label: string; hint?: string };

// Refresh action next to the first-column identity. Rendered as a small
// round pill button so it reads as interactive at a glance; state
// (idle / loading / ok / err) drives the ring + fill colour and the
// icon's spin. Tooltip carries the summary + error detail.
function RefreshAction({
  cell,
  onClick,
  slowHint,
}: {
  cell: RefreshCell | null;
  onClick: () => void;
  slowHint?: string;
}) {
  // Shared shape — kept identical across states so the row's height
  // never jumps when a refresh transitions loading → ok → idle.
  const shell =
    "ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full " +
    "border font-mono text-[13px] leading-none";

  if (cell?.kind === "loading") {
    return (
      <span
        className={`${shell} border-accent bg-accent/15 text-accent`}
        title={cell.label}
        aria-live="polite"
      >
        <span className="inline-block animate-spin">↻</span>
      </span>
    );
  }
  if (cell?.kind === "ok") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${shell} border-up/60 bg-up/15 text-up transition hover:bg-up/25 active:scale-95`}
        title={`Refreshed · ${cell.label}${cell.hint ? " · " + cell.hint : ""} — click to run again`}
      >
        ↻
      </button>
    );
  }
  if (cell?.kind === "err") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${shell} border-down/60 bg-down/15 text-down transition hover:bg-down/25 active:scale-95`}
        title={`Failed · ${cell.hint ?? cell.label} — click to retry`}
      >
        ↻
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        `${shell} border-rule bg-white/5 text-mute transition ` +
        "hover:border-accent hover:bg-accent/10 hover:text-accent active:scale-95"
      }
      aria-label="Refresh"
      title={slowHint ? `Refresh — ${slowHint}` : "Refresh"}
    >
      ↻
    </button>
  );
}

function summarizeAccountResult(r: RefreshAccountOut): { label: string; hint: string } {
  if (!r.ok) return { label: "Failed", hint: r.error ?? "" };
  const parts: string[] = [];
  if (r.inserted != null) parts.push(`+${r.inserted}`);
  if (r.skipped_non_chakra != null && r.skipped_non_chakra > 0)
    parts.push(`skip ${r.skipped_non_chakra}`);
  parts.push(`${r.seconds_elapsed.toFixed(1)}s`);
  return { label: parts.join(" · "), hint: `${r.from_date} → ${r.to_date}` };
}

function summarizeBhavcopyResult(r: RefreshBhavcopyOut): { label: string; hint: string } {
  if (!r.ok) return { label: "Failed", hint: r.error ?? "" };
  const parts: string[] = [];
  if (r.dates_loaded != null && r.dates_loaded > 0) parts.push(`+${r.dates_loaded}d`);
  if (r.dates_skipped != null && r.dates_skipped > 0) parts.push(`skip ${r.dates_skipped}`);
  if (r.dates_failed != null && r.dates_failed > 0) parts.push(`miss ${r.dates_failed}`);
  if (r.rows_inserted != null && r.rows_inserted > 0) parts.push(`${r.rows_inserted} rows`);
  parts.push(`${r.seconds_elapsed.toFixed(1)}s`);
  return {
    label: parts.join(" · "),
    hint: `${r.from_date} → ${r.to_date} · ${r.dates_attempted} dates attempted` +
      (r.instruments_refreshed_rows ? ` · ${r.instruments_refreshed_rows} instrument rows` : ""),
  };
}

function summarizeMvsResult(r: RefreshMvsOut): { label: string; hint: string } {
  if (!r.ok) {
    const failed = r.steps.filter((s) => !s.ok).map((s) => s.mv_name).join(", ");
    return { label: "Partial", hint: `Failed: ${failed}` };
  }
  return {
    label: `${r.steps.length} MVs · ${r.seconds_elapsed.toFixed(1)}s`,
    hint: r.steps.map((s) => `${s.mv_name} ${s.seconds_elapsed.toFixed(1)}s`).join(" · "),
  };
}

export default function DataFreshnessPage() {
  const [data, setData] = useState<DataFreshness | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Refresh state per row. Account maps keyed by account_id (a number
  // that stringifies well); MV map keyed by mv_name. All MV rows share
  // the same refresh result since the endpoint updates every MV at once.
  const [multiRefresh, setMultiRefresh] = useState<Map<number, RefreshCell>>(new Map());
  const [kanhojiRefresh, setKanhojiRefresh] = useState<Map<number, RefreshCell>>(new Map());
  // Garuda shares the Multitrade Playwright pull (one MT_X_TRADE download that
  // the loader splits by dealer.trading_system) but tracks its own row state.
  const [garudaRefresh, setGarudaRefresh] = useState<Map<number, RefreshCell>>(new Map());
  const [bhavRefresh, setBhavRefresh] = useState<Map<string, RefreshCell>>(new Map());
  const [mvRefresh, setMvRefresh] = useState<RefreshCell | null>(null);

  useEffect(() => {
    // /me tells us which user_group the caller is in.
    //   admin / super_admin / manager  → refresh buttons render (write)
    //   dealer / user                  → read-only view of their scoped
    //                                    accounts (backend filters by
    //                                    allowed_account_ids; no 403)
    // Manager was widened from read-only to write in
    // chakra_dashboard#125 alongside the reports/data-freshness widen.
    api.me().then(setMe).catch(() => setMe(null));
    api.dataFreshness()
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, []);

  const canWrite =
    me?.user_group === "admin"
    || me?.user_group === "super_admin"
    || me?.user_group === "manager";

  // Small helpers to fire+track a refresh POST. On completion, re-fetch
  // the freshness snapshot so the row's Last-trade / age columns update
  // alongside the pill result.
  async function fireAccountRefresh(
    kind: "Multitrade" | "Kanhoji" | "Garuda",
    accountId: number,
  ) {
    const setter =
      kind === "Multitrade" ? setMultiRefresh : kind === "Garuda" ? setGarudaRefresh : setKanhojiRefresh;
    setter((m) => new Map(m).set(accountId, { kind: "loading", label: "Pulling…" }));
    try {
      // Multitrade and Garuda are the same physical pull (one Playwright
      // MT_X_TRADE download; the loader attributes each fill by dealer).
      const call = kind === "Kanhoji" ? api.refreshAccountKanhoji : api.refreshAccountMultitrade;
      const res = await call(accountId);
      const { label, hint } = summarizeAccountResult(res);
      setter((m) => new Map(m).set(accountId, res.ok ? { kind: "ok", label, hint } : { kind: "err", label, hint }));
      if (res.ok) {
        const fresh = await api.dataFreshness();
        setData(fresh);
      }
    } catch (e) {
      setter((m) =>
        new Map(m).set(accountId, {
          kind: "err",
          label: "Failed",
          hint: e instanceof Error ? e.message : String(e),
        }),
      );
    }
  }

  async function fireBhavcopyRefresh(exchange: string) {
    setBhavRefresh((m) => new Map(m).set(exchange, { kind: "loading", label: "Pulling…" }));
    try {
      const res = await api.refreshBhavcopy(exchange);
      const { label, hint } = summarizeBhavcopyResult(res);
      setBhavRefresh((m) =>
        new Map(m).set(exchange, res.ok ? { kind: "ok", label, hint } : { kind: "err", label, hint }),
      );
      if (res.ok) {
        const fresh = await api.dataFreshness();
        setData(fresh);
      }
    } catch (e) {
      setBhavRefresh((m) =>
        new Map(m).set(exchange, {
          kind: "err",
          label: "Failed",
          hint: e instanceof Error ? e.message : String(e),
        }),
      );
    }
  }

  async function fireMvsRefresh() {
    setMvRefresh({ kind: "loading", label: "Refreshing MVs…" });
    try {
      const res = await api.refreshMvs();
      const { label, hint } = summarizeMvsResult(res);
      setMvRefresh(res.ok ? { kind: "ok", label, hint } : { kind: "err", label, hint });
      if (res.ok) {
        const fresh = await api.dataFreshness();
        setData(fresh);
      }
    } catch (e) {
      setMvRefresh({
        kind: "err",
        label: "Failed",
        hint: e instanceof Error ? e.message : String(e),
      });
    }
  }

  if (err) return <p className="text-sm text-down">{err}</p>;
  if (!data) return <p className="eyebrow text-mute">Loading…</p>;

  const serverNow = data.server_now;

  // Same column shape both sections — factored so the Refresh column
  // knows which trading_system to POST for.
  function makeAcctCols(kind: "Multitrade" | "Kanhoji" | "Garuda"): ColumnConfig<AccountFreshnessRow>[] {
    const stateMap = kind === "Multitrade" ? multiRefresh : kind === "Garuda" ? garudaRefresh : kanhojiRefresh;
    return [
      {
        key: "account",
        header: "Account",
        cell: (r) => (
          <span className="inline-flex items-baseline">
            <Link href={`/accounts/${r.account_id}`} className="hover:text-accent">
              <span className="font-mono text-sm text-foreground">{r.name}</span>{" "}
              <span className="text-xs text-mute">{r.account}</span>
            </Link>
            {canWrite && (
              <RefreshAction
                cell={stateMap.get(r.account_id) ?? null}
                onClick={() => fireAccountRefresh(kind, r.account_id)}
                slowHint={kind === "Kanhoji" ? "~5s" : "~60s (Playwright + captcha)"}
              />
            )}
          </span>
        ),
        sortAccessor: (r) => r.name,
        filterable: true,
        filterValues: (r) => `${r.name} ${r.account}`,
      },
      {
        key: "first_trade_date",
        header: "First trade",
        cell: (r) => fmtDate(r.first_trade_date),
        sortAccessor: (r) => r.first_trade_date ?? "",
        exportValue: (r) => r.first_trade_date ?? "",
      },
      {
        key: "last_trade_date",
        header: "Last trade",
        cell: (r) => fmtDate(r.last_trade_date),
        sortAccessor: (r) => r.last_trade_date ?? "",
        exportValue: (r) => r.last_trade_date ?? "",
      },
      {
        key: "trade_age",
        header: "Trade age",
        cell: (r) => <AgeBadge days={daysAgo(r.last_trade_date, serverNow)} warn={7} />,
        sortAccessor: (r) => daysAgo(r.last_trade_date, serverNow) ?? -1,
        exportValue: (r) => daysAgo(r.last_trade_date, serverNow) ?? "",
      },
      {
        key: "num_trades",
        header: "# Trades",
        align: "right",
        cell: (r) => fmtInt(r.num_trades),
        sortAccessor: (r) => r.num_trades,
        exportValue: (r) => r.num_trades,
      },
      {
        key: "last_margin_effective_date",
        header: "Last margin",
        cell: (r) => fmtDate(r.last_margin_effective_date),
        sortAccessor: (r) => r.last_margin_effective_date ?? "",
        exportValue: (r) => r.last_margin_effective_date ?? "",
      },
      {
        key: "last_margin_rs",
        header: "Margin ₹",
        align: "right",
        cell: (r) => (r.last_margin_rs ? fmtRupees(r.last_margin_rs) : "—"),
        sortAccessor: (r) => (r.last_margin_rs ? Number(r.last_margin_rs) : 0),
        exportValue: (r) => (r.last_margin_rs ? Number(r.last_margin_rs) : 0),
      },
    ];
  }

  const bhavCols: ColumnConfig<BhavcopyFreshnessRow>[] = [
    {
      key: "exchange",
      header: "Exchange",
      cell: (r) => (
        <span className="inline-flex items-baseline">
          <Pill variant="outline">{r.exchange}</Pill>
          {canWrite && (
            <RefreshAction
              cell={bhavRefresh.get(r.exchange) ?? null}
              onClick={() => fireBhavcopyRefresh(r.exchange)}
              slowHint="pulls missing dates + refreshes instruments · ~5–30s"
            />
          )}
        </span>
      ),
      sortAccessor: (r) => r.exchange,
      filterable: true,
      filterValues: (r) => r.exchange,
    },
    {
      key: "last_trade_date",
      header: "Last trade_date",
      cell: (r) => fmtDate(r.last_trade_date),
      sortAccessor: (r) => r.last_trade_date,
      exportValue: (r) => r.last_trade_date,
    },
    {
      key: "age",
      header: "Age",
      cell: (r) => <AgeBadge days={daysAgo(r.last_trade_date, serverNow)} warn={3} />,
      sortAccessor: (r) => daysAgo(r.last_trade_date, serverNow) ?? -1,
      exportValue: (r) => daysAgo(r.last_trade_date, serverNow) ?? "",
    },
    {
      key: "num_dates",
      header: "# Distinct dates",
      align: "right",
      cell: (r) => fmtInt(r.num_dates),
      sortAccessor: (r) => r.num_dates,
      exportValue: (r) => r.num_dates,
    },
  ];

  const mvCols: ColumnConfig<MvStatusRow>[] = [
    {
      key: "mv_name",
      header: "MV",
      // All MV rows share one refresh state — the endpoint refreshes
      // every MV at once (`sp_refresh_pnl_pipeline` + prerequisites),
      // so any row's ↻ triggers the same action and every row lights
      // up together.
      cell: (r) => (
        <span className="inline-flex items-baseline">
          {r.mv_name}
          {canWrite && (
            <RefreshAction cell={mvRefresh} onClick={fireMvsRefresh} slowHint="refreshes all MVs · ~30s" />
          )}
        </span>
      ),
      sortAccessor: (r) => r.mv_name,
      filterable: true,
      filterValues: (r) => r.mv_name,
    },
    {
      key: "last_refreshed_at",
      header: "Last refresh",
      cell: (r) => r.last_refreshed_at ?? "—",
      sortAccessor: (r) => r.last_refreshed_at ?? "",
      exportValue: (r) => r.last_refreshed_at ?? "",
    },
    {
      key: "last_row_count",
      header: "Row count",
      align: "right",
      cell: (r) => (r.last_row_count !== null ? fmtInt(r.last_row_count) : "—"),
      sortAccessor: (r) => r.last_row_count ?? -1,
      exportValue: (r) => r.last_row_count ?? "",
    },
  ];

  return (
    <Page
      eyebrow={<Crumbs items={[{ label: "Admin tools" }, { label: "Data freshness" }]} />}
      title={<>Data <em className="italic">freshness</em></>}
      actions={<span className="eyebrow text-mute">DB now: <span className="num text-foreground">{data.server_now}</span></span>}
    >
      <Section title="Accounts — Multitrade" aside={`${data.accounts_multitrade.length} active`}>
        <DataTable
          tableKey="freshness-accounts-multitrade"
          stickyFirstColumn
          data={data.accounts_multitrade}
          columns={makeAcctCols("Multitrade")}
          rowKey={(r) => String(r.account_id)}
        />
      </Section>

      <Section title="Accounts — Garuda" aside={`${data.accounts_garuda.length} active`}>
        <DataTable
          tableKey="freshness-accounts-garuda"
          stickyFirstColumn
          data={data.accounts_garuda}
          columns={makeAcctCols("Garuda")}
          rowKey={(r) => String(r.account_id)}
        />
      </Section>

      <Section title="Accounts — Kanhoji" aside={`${data.accounts_kanhoji.length} active`}>
        <DataTable
          tableKey="freshness-accounts-kanhoji"
          stickyFirstColumn
          data={data.accounts_kanhoji}
          columns={makeAcctCols("Kanhoji")}
          rowKey={(r) => String(r.account_id)}
        />
      </Section>

      <Section title="Bhavcopy ingest">
        <DataTable
          tableKey="freshness-bhavcopy"
          stickyFirstColumn
          data={data.bhavcopy}
          columns={bhavCols}
          rowKey={(r) => r.exchange}
        />
      </Section>

      <Section title="Materialized views">
        {data.materialized_views.length === 0 ? (
          <p className="eyebrow text-mute">No mv_status rows.</p>
        ) : (
          <DataTable
            tableKey="freshness-mvs"
            stickyFirstColumn
            data={data.materialized_views}
            columns={mvCols}
            rowKey={(r) => r.mv_name}
          />
        )}
      </Section>
    </Page>
  );
}
